import { Feature, MultiPolygon, Polygon, Position } from "geojson";

/**
 * Polygon inputs supported by the seeded map placement helpers.
 */
export type SupportedPolygon = Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon;

/**
 * Coordinate precision used when generating deterministic seed material.
 *
 * The API rounds persisted coordinates to 8 decimal places. Locally-created coordinates can contain more precision
 * before they are saved, for example -0.5639982568238793 locally and -0.56399826 after reload. Normalising to the
 * persisted precision makes seeded placement and rotation stable before and after the object is saved.
 */
export const COORDINATE_SEED_DECIMAL_PLACES = 8;

/**
 * Rounds a coordinate value to the precision used for seeded map behaviour.
 *
 * @param value Coordinate value to normalise.
 * @param decimalPlaces Number of decimal places to keep.
 * @returns Numeric coordinate rounded to the requested precision.
 */
export function normaliseCoordinateValue(value: number, decimalPlaces = COORDINATE_SEED_DECIMAL_PLACES): number {
    return Number(value.toFixed(decimalPlaces));
}

/**
 * Formats a coordinate value for string-based seed generation.
 *
 * This keeps trailing zeroes so equivalent coordinates always produce the same seed string shape.
 *
 * @param value Coordinate value to normalise and format.
 * @param decimalPlaces Number of decimal places to keep.
 * @returns Fixed precision coordinate string.
 */
export function formatCoordinateValueForSeed(value: number, decimalPlaces = COORDINATE_SEED_DECIMAL_PLACES): string {
    return normaliseCoordinateValue(value, decimalPlaces).toFixed(decimalPlaces);
}

/**
 * Formats a longitude/latitude coordinate for deterministic seed generation.
 *
 * @param coordinate Coordinate as [longitude, latitude].
 * @returns Stable fixed-precision coordinate seed string.
 */
export function formatCoordinateForSeed(coordinate: [number, number]): string {
    const [longitude, latitude] = coordinate;

    if (longitude == null || latitude == null) {
        throw new Error("Coordinate must include longitude and latitude values");
    }

    return `${formatCoordinateValueForSeed(longitude)},${formatCoordinateValueForSeed(latitude)}`;
}

/**
 * Returns a polygon or multipolygon with all coordinate values rounded to the seed precision.
 *
 * Random placement uses this normalised geometry for its seed, bounding box, and containment checks so locally-created
 * geometry and API-returned geometry produce the same deterministic point layout.
 *
 * @param inputPolygon Polygon, multipolygon, or feature containing one.
 * @returns Copy of the input polygon with normalised coordinate values.
 */
export function normalisePolygonCoordinates(inputPolygon: SupportedPolygon): SupportedPolygon {
    if (inputPolygon.type === "Feature") {
        return {
            ...inputPolygon,
            geometry: normaliseGeometryCoordinates(inputPolygon.geometry),
        };
    }

    return normaliseGeometryCoordinates(inputPolygon);
}

/**
 * Copies a polygon geometry and normalises each nested coordinate.
 *
 * @param geometry Polygon or multipolygon geometry to normalise.
 * @returns Geometry copy with normalised coordinate values.
 */
function normaliseGeometryCoordinates<T extends Polygon | MultiPolygon>(geometry: T): T {
    return {
        ...geometry,
        coordinates: normaliseCoordinates(geometry.coordinates) as T["coordinates"],
    };
}

/**
 * Recursively walks the nested coordinate arrays used by polygon and multipolygon geometries.
 *
 * @param coordinates Coordinate array at any polygon nesting level.
 * @returns Coordinate array with all numeric values rounded to the seed precision.
 */
function normaliseCoordinates(coordinates: Position | Position[][] | Position[][][]): unknown {
    if (typeof coordinates[0] === "number") {
        return (coordinates as Position).map((coordinateValue) => normaliseCoordinateValue(coordinateValue));
    }

    return (coordinates as Position[][] | Position[][][]).map((coordinate) => normaliseCoordinates(coordinate as Position | Position[][]));
}
